/**
 * The questions the API asks before it will write something (Plans D3 and D4).
 *
 * Two rules refuse a request *once* and then take yes for an answer: attendance
 * for someone with no assignment, and a commitment that clashes with one already
 * there. Both work the same way — a 409 carrying what the problem is, and a retry
 * carrying a flag that says staff saw it.
 *
 * **One record can need both**, and that is what this hook exists for. Answering
 * them one dialog at a time means staff confirm, watch the form fail again, and
 * confirm something else — which reads as the system arguing with them. So the
 * questions accumulate: each 409 is remembered, all of them are shown together,
 * and one "yes" sends every flag that has been earned.
 *
 * The flags are never sent on a first attempt. That is the whole point of the
 * flow: the warning has to be seen before it can be waved through, and the
 * acceptance is what lands in the audit trail.
 */

import { useState } from 'react';
import { API_ERROR_CODE } from '@parinaam-vms/shared';
import { ApiRequestError } from '../services/api/client';

/** Which flag answers which question. Adding a rule means adding a line here. */
const FLAG_FOR: Partial<Record<string, 'allow_unassigned' | 'allow_overlap'>> = {
  [API_ERROR_CODE.ASSIGNMENT_REQUIRED]: 'allow_unassigned',
  [API_ERROR_CODE.OVERLAP_DETECTED]: 'allow_overlap',
};

export interface ConfirmationFlags {
  allow_unassigned?: true;
  allow_overlap?: true;
}

export function useConfirmations() {
  const [asked, setAsked] = useState<ApiRequestError[]>([]);

  return {
    /** Every question raised so far, in the order they were asked. */
    asked,

    /** The flags the next attempt should carry — one per question already shown. */
    flags: asked.reduce<ConfirmationFlags>((carried, error) => {
      const flag = FLAG_FOR[error.code];
      return flag ? { ...carried, [flag]: true } : carried;
    }, {}),

    /**
     * Remember a question, if that is what this error is.
     *
     * Returns whether it was one, synchronously — the caller needs to know *now*
     * whether to show a warning or a failure, and React state has not settled yet.
     */
    remember(error: unknown): boolean {
      if (!(error instanceof ApiRequestError) || !FLAG_FOR[error.code]) return false;

      setAsked((current) =>
        // The same question twice is the same question. It can happen when a
        // retry fails for an unrelated reason and is tried again.
        current.some((seen) => seen.code === error.code) ? current : [...current, error]
      );
      return true;
    },

    /** Back to asking nothing — on cancel, or once the write has gone through. */
    reset() {
      setAsked([]);
    },
  };
}
